import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import type { ReactNode } from 'react';

interface ExpandedSummaryEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editedSummary?: string | null;
  fallbackText: string;
  onEdit?: (value: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  fontSize: number;
  title: string;
  /** The composer panel's note body (beta), shown instead of the plain textarea. */
  children?: ReactNode;
}

export const ExpandedSummaryEditor = ({
  open,
  onOpenChange,
  editedSummary,
  fallbackText,
  onEdit,
  onSave,
  isSaving,
  fontSize,
  title,
  children,
}: ExpandedSummaryEditorProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="flex h-[90vh] max-w-6xl flex-col">
      <DialogHeader>
        <DialogTitle className="font-medium text-muted-foreground text-sm uppercase tracking-wider">
          {title}
        </DialogTitle>
      </DialogHeader>
      {children ? (
        // Padding leaves room for the editing textarea's outset ring.
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">{children}</div>
      ) : (
        <Textarea
          value={editedSummary ?? fallbackText}
          onChange={(e) => onEdit?.(e.target.value)}
          className="flex-1 resize-none font-sans leading-relaxed"
          style={{ fontSize }}
          placeholder="Samantekt..."
        />
      )}
      <div className="flex justify-end gap-2">
        {onSave && editedSummary && (
          <Button
            size="sm"
            onClick={() => {
              onSave();
              onOpenChange(false);
            }}
            disabled={isSaving}
            className="gap-2 bg-blue-600 text-white hover:bg-blue-700"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Vista
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>
          Loka
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
